import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { QnSbnService } from '../services/qn-sbn-service.service';
import { OneSnackBarService, OneSnackBarType } from '@one/angular-kit/modal';
import { FileFormatDialogComponent } from '../notification/file-format-dialog/file-format-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { Chart } from 'chart.js';
import { NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { formatDate } from '@angular/common';

export const PICK_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
}

@Injectable()
class PickDateAdapter extends NativeDateAdapter {
  public format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      return formatDate(date, 'dd-MMM-yyyy', this.locale);
    } else {
      return date.toDateString();
    }
  }
}

function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const from = control.get('dateFrom')?.value;
    const to = control.get('dateTo')?.value;
    if (from && to && from > to) {
      return { dateRangeInvalid: true };
    }
    return null;
  };
}

function formatDateOnly(dateValue: Date): string {
  if (dateValue) {
    const date = new Date(dateValue);
    const day = date.toLocaleString('default', { day: '2-digit' });
    const month = date.toLocaleString('default', { month: '2-digit' });
    const year = date.toLocaleString('default', { year: 'numeric' });
    return year + '-' + month + '-' + day;
  } else {
    return '';
  }
}

@Component({
  selector: 'dl-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: PickDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS }
  ]
})

export class DashboardComponent implements OnInit, AfterViewInit {
  
  @ViewChild('notificationTypeChart') notificationTypeChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('accountStatusChart') accountStatusChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('accountDeliveryChart') accountDeliveryChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('accountFeedbackChart') accountFeedbackChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('monthlyChart') monthlyChart!: ElementRef<HTMLCanvasElement>;

  // Chart instances
  notificationTypeChartInstance!: Chart;
  accountStatusChartInstance!: Chart;
  accountDeliveryChartInstance!: Chart;
  accountFeedbackChartInstance!: Chart;
  monthlyChartInstance!: Chart;
  
  dbTableData = [];

  // Chart data for display
  notificationTypeData = { 
    total: 0, 
    legend: []
  };
  accountStatusData = { 
    total: 0, 
    legend: [] as any[]
  };
  accountDeliveryData = { 
    total: 0, 
    legend: []
  };
  accountFeedbackData = { 
    total: 0, 
    legend: []
  };

  form: FormGroup;
  types =  ['All', 'SBN', 'QN', 'MN', 'SN', 'SNR'];
  accountStatuses =['All', 'Send', 'Delivery Failed', 'Delivered', 'Reminder', 'Read', 'Feedback Overdue', 'Feedback Received']
  countries = [];

  disabledCountryOptions: Set<string> = new Set();
  disabledStatusOptions: Set<string> = new Set();
  disabledTypeOptions: Set<string> = new Set();

  constructor(
    private fb: FormBuilder,
    public qnsbnService: QnSbnService,
    public cdr: ChangeDetectorRef,
    private readonly oneSnackBar: OneSnackBarService,
    public readonly dialog?: MatDialog, 
  ){}

  ngOnInit() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.countries = [ 'All', ...this.qnsbnService.getUserCountries() ]
    this.form = this.fb.group(
      {
        dateFrom: [thirtyDaysAgo, Validators.required],
        dateTo: [today, Validators.required],
        notification: [''],
        account: [''],
        type: [['All'], Validators.required],
        accountStatus: [['All'], Validators.required],
        country: [['All'], Validators.required],
      },
      { validators: dateRangeValidator() }
    );

    this.onTypeChange();
    this.onStatusChange();
    this.onCountryChange();
    this.search();
  }

  ngAfterViewInit() {
    this.initializeCharts();
  }

  initializeCharts() {
    this.createNotificationTypeChart();
    this.createAccountStatusChart();
    this.createAccountDeliveryChart();
    this.createAccountFeedbackChart();
    this.createMonthlyChart();
  }

  createNotificationTypeChart() {
    const ctx = this.notificationTypeChart.nativeElement.getContext('2d');
    this.notificationTypeChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['SBN', 'QN', 'MN', 'SN', 'SNR'],
        datasets: [{
          data: [0, 0, 0, 0, 0], // Will be updated with real data
          backgroundColor: ['#FF6B9D', '#8FBC8F', '#FFA500', '#4A90E2', '#9b59b6'],
          borderWidth: 0,
          cutout: '70%'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        }
        // plugins: {
        // }
      }
    });
  }

  createAccountStatusChart() {
    const ctx = this.accountStatusChart.nativeElement.getContext('2d');
    this.accountStatusChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{
          data: [], // Will be updated with real data
          backgroundColor: [],
          borderWidth: 0,
          cutout: '70%'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        }
        // plugins: {
        // }
      }
    });
  }

  createAccountDeliveryChart() {
    const ctx = this.accountDeliveryChart.nativeElement.getContext('2d');
    this.accountDeliveryChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Delivered', 'Delivery failures'],
        datasets: [{
          data: [0, 0], // Will be updated with real data
          backgroundColor: ['#2ecc71', '#95a5a6'],
          borderWidth: 0,
          cutout: '70%'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        }
        // plugins: {
        // }
      }
    });
  }

  createAccountFeedbackChart() {
    const ctx = this.accountFeedbackChart.nativeElement.getContext('2d');
    this.accountFeedbackChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Submitted', 'Pending'],
        datasets: [{
          data: [0, 0], // Will be updated with real data
          backgroundColor: ['#3498db', '#95a5a6'],
          borderWidth: 0,
          cutout: '70%'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        }
        // plugins: {
        // }
      }
    });
  }

  createMonthlyChart() {
    const ctx = this.monthlyChart.nativeElement.getContext('2d');
    this.monthlyChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Jan-2025', 'Feb-2025', 'Mar-2025', 'Apr-2025', 'May-2025', 'Jun-2025', 'Jul-2025'],
        datasets: [
          {
            label: 'SBN',
            data: [12, 18, 30, 40, 8, 6, 25],
            backgroundColor: '#FF6B9D',
            borderRadius: 0
          },
          {
            label: 'QN',
            data: [8, 20, 25, 35, 12, 8, 25],
            backgroundColor: '#8FBC8F',
            borderRadius: 0
          },
          {
            label: 'SN',
            data: [15, 5, 10, 15, 5, 5, 70],
            backgroundColor: '#4A90E2',
            borderRadius: 0
          },
          {
            label: 'SNR',
            data: [5, 2, 8, 10, 3, 2, 15],
            backgroundColor: '#9b59b6',
            borderRadius: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            grid: {
              display: true,
              color: '#E0E0E0',
              drawBorder: false
            },
            ticks: {
              color: '#666666',
              font: {
                size: 11
              }
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: 120,
            grid: {
              display: true,
              color: '#E0E0E0',
              drawBorder: false
            },
            ticks: {
              stepSize: 20,
              color: '#666666',
              font: {
                size: 11
              }
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'center',
            labels: {
              boxWidth: 15,
              boxHeight: 10,
              padding: 15,
              usePointStyle: false,
              font: {
                size: 12,
                weight: '500'
              },
              color: '#333333'
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#cccccc',
            borderWidth: 1,
            callbacks: {
              title: function(context) {
                return context[0].label;
              },
              label: function(context) {
                const datasetLabel = context.dataset.label || '';
                const value = context.parsed.y;
                return `${datasetLabel}: ${value}`;
              }
            }
          }
        },
        elements: {
          bar: {
            borderWidth: 0
          }
        }
      }
    });
  }

// Helpers
  clearDate(controlName: string) {
    this.form.get(controlName)?.setValue(null);
  }

  clearNotification() {
    this.form.get('notification')?.setValue('');
  }

  clearAccount() {
    this.form.get('account')?.setValue('');
  }

  excludeAll(arr: string[]): string[] {
    return arr.filter(item => item !== 'All');
  }

// API Calls
  search() {
    if (this.form.valid) {

      const searchObj = {
        "dateFrom": formatDateOnly(this.form.get('dateFrom').value),
        "dateTo": formatDateOnly(this.form.get('dateTo').value),
        "notification": this.form.get('notification').value,
        "account": this.form.get('account').value,
        "type": this.excludeAll(this.form.get('type').value),
        "accountStatus": this.excludeAll(this.form.get('accountStatus').value),
        "country": this.excludeAll(this.form.get('country').value),
      }

      this.fetchStatisticsReport(searchObj);

    } else {
      this.form.markAllAsTouched();
    }
  }

  fetchStatisticsReport(obj){
    this.qnsbnService.statisticsReportDashboard(obj).subscribe(
      res => {
        if (res?.statusCode && res.statusCode !== 200) {
          let errorMessage = 'Please try again later';
          
          if (res.statusCode === 404) {
            const bodyData = typeof res.body === 'string' ? JSON.parse(res.body) : res.body;
            errorMessage = bodyData?.message || 'Please try again later';
          }
          
          this.oneSnackBar.open(errorMessage, OneSnackBarType.ERROR, '', { duration: 3000 });
          this.dbTableData = []; // Clear table data
          this.resetChartsToInitialState(); // Reset charts to initial state
          return;
        }

        // Handle successful response
        const transformedData = res?.body?.accountsData?.map((item: any) => ({
          dateCreated: item.DateCreated,
          country: item.Country,
          type: item.Type,
          localId: item.LocalID,
          accountId: item.AccountID,
          accountName: item.AccountName,
          accountStatus: item.AccountStatus,
          delivery: item.Delivery,
          feedback: item.Feedback,
          globalId: item.GlobalID,
          dateSent: item.DateSent,
          dateDelivered: item.DateDelivered,
          dateRead: item.DateRead,
          dateFeedback: item.DateFeedback,
          deliveryFailures: item.DeliveryFailures,
          daysToOpen: item.DaysToOpen,
          daysToSubmit: item.DaysToSubmit
        })) || [];
        
        this.dbTableData = transformedData;
        
        // Check for empty accounts data or no statistics data
        if (transformedData.length === 0 || !res?.body?.statisticsData) {
          this.resetChartsToInitialState();
        } else {
          // Update charts with real data
          this.updateChartsWithData(res.body.statisticsData);
        }
      },
      err => {
        // Handle HTTP errors (network issues, server errors, etc.)
        let errorMessage = 'Please try again later';
        
        if (err?.error?.message) {
          errorMessage = err.error.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        this.oneSnackBar.open(errorMessage, OneSnackBarType.ERROR, '', { duration: 3000 });
        this.dbTableData = []; // Clear table data on error
        this.resetChartsToInitialState(); // Reset charts to initial state on error
      }
    );
  }

  updateChartsWithData(statisticsData: any) {
    // Update Notification Type Chart (Pie Chart)
    if (statisticsData.notificationType && this.notificationTypeChartInstance) {
      const typeData = statisticsData.notificationType;
      
      // Extract individual type counts
      const sbn = Number(typeData.SBN) || 0;
      const qn = Number(typeData.QN) || 0;
      const mn = Number(typeData.MN) || 0;
      const sn = Number(typeData.SN) || 0;
      const snr = Number(typeData.SNR) || 0;
      const total = sbn + qn + mn + sn + snr;
      
      // Update component data with dynamic legend
      this.notificationTypeData = {
        total,
        legend: [
          { label: 'SBN', count: sbn, color: '#FF6B9D' },
          { label: 'QN', count: qn, color: '#8FBC8F' },
          { label: 'MN', count: mn, color: '#FFA500' },
          { label: 'SN', count: sn, color: '#4A90E2' },
          { label: 'SNR', count: snr, color: '#9b59b6' }
        ]
      };
      
      // Update chart
      this.notificationTypeChartInstance.data.datasets[0].data = [sbn, qn, mn, sn, snr];
      this.notificationTypeChartInstance.update();
    }

    // Update Account Status Chart (Pie Chart)
    if (statisticsData.accountStatus && this.accountStatusChartInstance) {
      const statusData = statisticsData.accountStatus;
      
      // Create dynamic legend based on available status data
      const statusItems = [];
      const chartData = [];
      const colors = ['#4ecdc4', '#95a5a6', '#2ecc71', '#3498db', '#e74c3c', '#f39c12', '#9b59b6'];
      let colorIndex = 0;
      
      // Process all available statuses from API response
      Object.keys(statusData).forEach(key => {
        const count = Number(statusData[key]) || 0;
        if (count > 0) {
          statusItems.push({
            label: key,
            count: count,
            color: colors[colorIndex % colors.length]
          });
          chartData.push(count);
          colorIndex++;
        }
      });
      
      const total = statusItems.reduce((sum, item) => sum + item.count, 0);
      
      // Update component data
      this.accountStatusData = { total, legend: statusItems };
      
      // Update chart colors and data
      this.accountStatusChartInstance.data.datasets[0].backgroundColor = statusItems.map(item => item.color);
      this.accountStatusChartInstance.data.datasets[0].data = chartData;
      this.accountStatusChartInstance.data.labels = statusItems.map(item => item.label);
      this.accountStatusChartInstance.update();
    }

    // Update Account Delivery Chart (Pie Chart)
    if (statisticsData.accountDelivery && this.accountDeliveryChartInstance) {
      const deliveryData = statisticsData.accountDelivery;
      
      const delivered = Number(deliveryData.delivered) || 0;
      const failed = Number(deliveryData.failed) || 0;
      const total = delivered + failed;
      
      // Update component data with dynamic legend
      this.accountDeliveryData = {
        total,
        legend: [
          { label: 'Delivered', count: delivered, color: '#2ecc71' },
          { label: 'Delivery failures', count: failed, color: '#95a5a6' }
        ]
      };
      
      // Update chart
      this.accountDeliveryChartInstance.data.datasets[0].data = [delivered, failed];
      this.accountDeliveryChartInstance.update();
    }

    // Update Account Feedback Chart (Pie Chart)
    if (statisticsData.accountFeedback && this.accountFeedbackChartInstance) {
      const feedbackData = statisticsData.accountFeedback;
      
      const submitted = Number(feedbackData.submitted) || 0;
      const pending = Number(feedbackData.pending) || 0;
      const total = submitted + pending;
      
      // Update component data with dynamic legend
      this.accountFeedbackData = {
        total,
        legend: [
          { label: 'Submitted', count: submitted, color: '#3498db' },
          { label: 'Pending', count: pending, color: '#95a5a6' }
        ]
      };
      
      // Update chart
      this.accountFeedbackChartInstance.data.datasets[0].data = [submitted, pending];
      this.accountFeedbackChartInstance.update();
    }

    // Update Monthly Bar Chart
    if (statisticsData.notificationsByMonth && this.monthlyChartInstance) {
      const monthlyData = statisticsData.notificationsByMonth;
      
      // Update labels: ["Jul 2025", "Aug 2025"]
      this.monthlyChartInstance.data.labels = monthlyData.labels;
      
      // Update datasets with your data structure
      const datasets: any[] = [];
      monthlyData.datasets.forEach((dataset: any) => {
        let backgroundColor = '#95a5a6'; // Default gray
        
        // Color mapping for your notification types
        if (dataset.label === 'QN') {
          backgroundColor = '#8FBC8F'; // Green for QN
        } else if (dataset.label === 'SN') {
          backgroundColor = '#4A90E2'; // Blue for SN
        } else if (dataset.label === 'MN') {
          backgroundColor = '#FFA500'; // Orange for MN
        } else if (dataset.label === 'SBN') {
          backgroundColor = '#FF6B9D'; // Pink for SBN
        } else if (dataset.label === 'SNR') {
          backgroundColor = '#9b59b6'; // Purple for SNR
        }
        
        datasets.push({
          label: dataset.label,
          data: dataset.data, // QN: [35, 59], SN: [5, 3], MN: [2, 0]
          backgroundColor: backgroundColor,
          borderRadius: 0
        });
      });
      
      this.monthlyChartInstance.data.datasets = datasets;
      
      // Auto-adjust Y-axis based on max data value
      const allValues = monthlyData.datasets.flatMap((d: any) => d.data);
      const maxValue = Math.max(...allValues);
      const yAxisMax = Math.ceil(maxValue * 1.2 / 20) * 20; // Round up to nearest 20
      this.monthlyChartInstance.options.scales.y.max = Math.max(yAxisMax, 40);
      
      this.monthlyChartInstance.update();
    }
  }

  resetChartsToInitialState() {
    // Reset to initial states matching lines 57-77
    this.notificationTypeData = { 
      total: 0, 
      legend: []
    };
    
    this.accountStatusData = { 
      total: 0, 
      legend: [] as any[]
    };
    
    this.accountDeliveryData = { 
      total: 0, 
      legend: []
    };
    
    this.accountFeedbackData = { 
      total: 0, 
      legend: []
    };

    // Update chart instances if they exist
    if (this.notificationTypeChartInstance) {
      this.notificationTypeChartInstance.data.datasets[0].data = [0, 0, 0, 0, 0];
      this.notificationTypeChartInstance.update();
    }
    
    if (this.accountStatusChartInstance) {
      this.accountStatusChartInstance.data.datasets[0].data = [];
      this.accountStatusChartInstance.data.labels = [];
      this.accountStatusChartInstance.data.datasets[0].backgroundColor = [];
      this.accountStatusChartInstance.update();
    }
    
    if (this.accountDeliveryChartInstance) {
      this.accountDeliveryChartInstance.data.datasets[0].data = [0, 0];
      this.accountDeliveryChartInstance.update();
    }
    
    if (this.accountFeedbackChartInstance) {
      this.accountFeedbackChartInstance.data.datasets[0].data = [0, 0];
      this.accountFeedbackChartInstance.update();
    }
    
    if (this.monthlyChartInstance) {
      this.monthlyChartInstance.data.labels = [];
      this.monthlyChartInstance.data.datasets = [];
      this.monthlyChartInstance.update();
    }
  }

// Downloads
  onDownload(): void {
    const downloadReport = this.dialog.open(FileFormatDialogComponent, { disableClose: true });
    downloadReport.afterClosed().subscribe(
      res => {
        if (res === 'csv') {
          this.csvData();
        }
        if (res === 'xlsx') {
          this.downloadExcel(this.dbTableData, 'Statistics Report');
        }
      },
      err => {
        this.oneSnackBar.open('Please try again later', OneSnackBarType.ERROR, '', { duration: 3000 });
      }
    );
  }

  downloadExcel(data, filename) {
    const wb = XLSX.utils.book_new();
    const statisticsReport = data.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });
    const ws = XLSX.utils.json_to_sheet(statisticsReport);
    XLSX.utils.book_append_sheet(wb, ws, 'Statistics Report');
    
    // Download File
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([this.stringToArrayBuffer(wbout)], { type: 'application/octet-stream' });

    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);

    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }

    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.xlsx');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  stringToArrayBuffer(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  }

  downloadCSV(data, filename, headerList) {
    let csvData = this.formatCSV(data, headerList);
    let blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);
    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  formatCSV(objArray, headerList) {
    let str = '';
    str += headerList + '\r\n';

    for (let i = 0; i < objArray.length; i++) {
      let line = '';
      line += objArray[i] || '';
      str += line + '\r\n';
    }

    let res = '';
    res = 'SEP=,' + '\r\n' + str;

    return res;
  }

  csvData(){
    const contacts = this.dbTableData.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });

    let headers = contacts.map((item) => Object.keys(item).toString());
    headers = Array.from(new Set(headers));
    
    const values = contacts.map((item) => {
      return Object.values(item).map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          const escapedValue = value.replace(/"/g, '""');
          return `"${escapedValue}"`;
        }
        return value;
      }).join(',');
    });
    
    this.downloadCSV(values, 'Statistics Report', headers);
  }

// Filters
  onCountryChange(): void {
    const selected = this.form.get('country').value || [];

    if (selected.includes('All')) {
      this.form.get('country').setValue([...this.countries]);
      this.disabledCountryOptions = new Set(this.form.get('country').value.filter(opt => opt !== 'All'));
    }

    if(!selected.includes('All')){
      const allSelected = selected.length === this.countries.length-1;
      const disabledOptions = this.disabledCountryOptions.size > 0;

      if (allSelected) {
        this.form.get('country').setValue([...this.countries]);
        this.disabledCountryOptions = new Set(this.form.get('country').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('country').reset();
        this.disabledCountryOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isCountryDisabled(option: string): boolean {
    return this.disabledCountryOptions.has(option);
  }

  onStatusChange(): void {
    const selected = this.form.get('accountStatus').value || [];

    if (selected.includes('All')) {
      this.form.get('accountStatus').setValue([...this.accountStatuses]);
      this.disabledStatusOptions = new Set(this.form.get('accountStatus').value.filter(opt => opt !== 'All'));
    }
    
    if(!selected.includes('All')){
      const allSelected = selected.length === this.accountStatuses.length-1;
      const disabledOptions = this.disabledStatusOptions.size > 0;

      if (allSelected) {
        this.form.get('accountStatus').setValue([...this.accountStatuses]);
        this.disabledStatusOptions = new Set(this.form.get('accountStatus').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('accountStatus').reset();
        this.disabledStatusOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isStatusDisabled(option: string): boolean {
    return this.disabledStatusOptions.has(option);
  }

  onTypeChange(): void {
    const selected = this.form.get('type').value || [];

    if (selected.includes('All')) {
      this.form.get('type').setValue([...this.types]);
      this.disabledTypeOptions = new Set(this.form.get('type').value.filter(opt => opt !== 'All'));
    } 

    if(!selected.includes('All')){
      const allSelected = selected.length === this.types.length-1;
      const disabledOptions = this.disabledTypeOptions.size > 0;

      if (allSelected) {
        this.form.get('type').setValue([...this.types]);
        this.disabledTypeOptions = new Set(this.form.get('type').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('type').reset();
        this.disabledTypeOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isTypeDisabled(option: string): boolean {
    return this.disabledTypeOptions.has(option);
  }

  public isAllSelected(FCN: string): boolean {
    const selected = this.form.get(FCN).value || [];
    if(FCN === 'country'){
      return selected.length === this.countries.length || selected.includes('all');
    }
    if(FCN === 'accountStatus'){
      return selected.length === this.accountStatuses.length || selected.includes('all');
    }
    if(FCN === 'type'){
      return selected.length === this.types.length || selected.includes('all');
    }
  }

  public selectedRolesDisplay(FCN: string){
    if (this.isAllSelected(FCN)) {
      return 'All';
    }
    return (this.form.get(FCN).value || []).join(', ');
  }

}
